import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { useNavigate } from "react-router-dom";
import {
  useGetBaseRateFileConversionImpactQuery,
  useSetBaseRateFileCarveoutFlagMutation,
} from "store/queries/baseRateFiles";
import { IBaseRateFileConversionImpactContract } from "types/baseRateFile";

export interface IConvertibleFile {
  id: string;
  displayName?: string;
  fileName?: string;
  isCarveout?: boolean;
}

interface IToastMessage {
  severity: "success" | "error";
  summary: string;
  detail: string;
  life?: number;
}

interface IUseBaseRateFileKindConversionArgs {
  /** Files whose conversion impact should be pre-fetched (one batched request). */
  fileIds: string[];
  /** Hospital account, when the library is embedded in a hospital — scopes contract links. */
  accountId?: string;
  showToast: (message: IToastMessage) => void;
}

export interface IConversionResult {
  updatedCount: number;
  isCarveout: boolean;
  /** Contracts whose link row the API moved — callers must not move those rows again. */
  migratedContracts: { id: string; label: string }[];
  outdatedContractIds: string[];
}

interface IOpenConvertDialogOptions {
  /** Extra caution shown in the confirm modal, from the caller's own context. */
  warning?: string;
  onConverted?: (isCarveout: boolean, result: IConversionResult) => void;
}

/**
 * CLE-2567 — shared "convert this file between base rate and carve-out" action,
 * used from the base rate file library and from a contract's rate calculations.
 *
 * Attached contracts do not block the conversion: each contract's link row is moved
 * to the table the new kind lives in, and Active contracts are flagged for
 * re-generation. Because that reaches live pricing, the dialog states up front
 * exactly which contracts are affected and which will need re-activating.
 */
export function useBaseRateFileKindConversion({
  fileIds,
  accountId,
  showToast,
}: IUseBaseRateFileKindConversionArgs) {
  const navigate = useNavigate();
  const [setCarveoutFlag] = useSetBaseRateFileCarveoutFlagMutation();

  const { data: impactData } = useGetBaseRateFileConversionImpactQuery(
    { ids: fileIds },
    { skip: fileIds.length === 0 }
  );

  const impactForFile = (fileId: string) =>
    impactData?.data?.[fileId] ?? { contracts: [], dropsDrgBreakdown: false };

  /** Contract detail route, hospital-scoped when embedded in a hospital. */
  const contractUrl = (id: string) =>
    accountId
      ? `/hospitals/view/${accountId}/contracts/${id}`
      : `/payer-rules/contracts/${id}`;

  const openConvertDialog = (
    file: IConvertibleFile,
    options: IOpenConvertDialogOptions = {}
  ) => {
    const toCarveout = !file.isCarveout;
    const target = toCarveout ? "carve-out" : "base rate";
    const label = file.displayName || file.fileName || "This file";
    const { contracts, dropsDrgBreakdown } = impactForFile(file.id);
    const activeContracts = contracts.filter((c) => c.status === "Active");
    const withOtherSources = contracts.filter(
      (c: IBaseRateFileConversionImpactContract) => c.otherRateSources > 0
    );

    const dialog = confirmDialog({
      message: (
        <div className="flex flex-column gap-2">
          <span>
            Convert <strong>{label}</strong> to a {target} file? This applies to
            every term and version of {file.fileName ?? label}.
          </span>

          {contracts.length > 0 && (
            <>
              <span>
                {contracts.length} contract
                {contracts.length === 1 ? "" : "s"} use
                {contracts.length === 1 ? "s" : ""} this file. Their rate rows
                will move to <strong>{target}</strong>, keeping the same rate.
                {activeContracts.length > 0 &&
                  ` ${activeContracts.length} active contract${
                    activeContracts.length === 1 ? "" : "s"
                  } will need re-activating to regenerate pricing.`}
              </span>
              <ul className="list-none p-0 m-0 flex flex-column gap-1">
                {contracts.map((contract) => (
                  <li key={contract.id}>
                    <Button
                      label={`${contract.label}${
                        contract.status === "Active"
                          ? ""
                          : ` (${contract.status})`
                      }`}
                      icon="pi pi-external-link"
                      iconPos="right"
                      className="p-button-link p-0"
                      onClick={() => {
                        dialog.hide();
                        navigate(contractUrl(contract.id));
                      }}
                    />
                  </li>
                ))}
              </ul>
            </>
          )}

          {toCarveout && withOtherSources.length > 0 && (
            <span className="text-orange-600">
              {withOtherSources.length} of these contracts also use other base
              rate files. As a carve-out, this file will start overriding their
              rates for the codes it lists instead of pricing alongside them.
            </span>
          )}

          {toCarveout && dropsDrgBreakdown && (
            <span className="text-orange-600">
              This file carries CMS per-DRG payment columns. Carve-out rows
              cannot hold them, so that breakdown will be dropped from the
              generated procedures.
            </span>
          )}

          {options.warning && (
            <span className="text-orange-600">{options.warning}</span>
          )}
        </div>
      ),
      header: "Confirm Conversion",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          const result = await setCarveoutFlag({
            id: file.id,
            isCarveout: toCarveout,
          }).unwrap();
          const movedCount = result.migratedContracts?.length ?? 0;
          showToast({
            severity: "success",
            summary: "File Converted",
            detail:
              movedCount > 0
                ? `Converted to a ${target} file (${
                    result.updatedCount
                  } version${
                    result.updatedCount === 1 ? "" : "s"
                  }). ${movedCount} contract${
                    movedCount === 1 ? "" : "s"
                  } updated; ${
                    result.outdatedContractIds?.length ?? 0
                  } need re-activating.`
                : `Converted to a ${target} file (${
                    result.updatedCount
                  } version${result.updatedCount === 1 ? "" : "s"} updated).`,
            life: movedCount > 0 ? 10000 : undefined,
          });
          options.onConverted?.(toCarveout, result);
        } catch (error: any) {
          showToast({
            severity: "error",
            summary: "Conversion Failed",
            detail:
              error?.data?.messages?.[0] ||
              error?.data?.message ||
              error?.data?.error ||
              `Failed to convert the file to a ${target} file.`,
            life: 10000,
          });
        }
      },
    });
  };

  return { openConvertDialog, impactForFile };
}
